/**
 * ExplanationPanel — the §24 verdict, rendered.
 *
 * The server has composed this envelope since lane B7a and no screen has ever
 * shown it: `TurnResponse.explanation` arrived on every turn and
 * `ConversationSurface` dropped it on the floor. This is the renderer.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THREE ARMS, THREE RENDERS, AND THE MIDDLE ONE IS WHY THIS FILE IS CAREFUL.
 *
 *   ABSENT    → `null`. Nothing. No heading, no empty panel, no "no reasoning
 *               was provided". Most answers in this product are a figure or a
 *               list and carry no explanation block; a panel that announced its
 *               own emptiness on all of them would be a warning on every
 *               ordinary answer, which is B7a's stated reason for keeping the
 *               field optional in the first place.
 *
 *   REJECTED  → one visible, announced sentence saying a check refused the
 *               model's reasoning, and which check. This is the arm that looks
 *               identical to ABSENT from a distance — both show no reasoning —
 *               and is not the same fact at all. A reader must be able to tell
 *               an answer whose reasoning FAILED A GUARD from one that never
 *               claimed reasoning.
 *
 *   COMPOSED  → the reasoning itself, with its own discriminants preserved:
 *               causes apart from coincidences apart from guesses, withheld
 *               apart from absent, and a date on the policy that was applied.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * NOTHING HERE IS RENDERED AS MARKUP, AND THAT IS A PROPERTY, NOT A HABIT.
 *
 * Every string below arrives from one of two untrusted places: the model
 * (factor labels, statements, `whyItMatters`, delta endpoints) or the tenant's
 * own doctrine corpus (rule text, source titles, publishers, citations), which
 * is fed by uploads and external ingestion. The founder's fifth ruling is
 * explicit that injection defences apply EVEN AFTER HUMAN APPROVAL — approval
 * establishes that a rule is enforceable, never that its text is safe to obey.
 *
 * So: every one of them goes through a React text node and there is no
 * `dangerouslySetInnerHTML` anywhere in this directory. That is asserted twice,
 * exactly as lane UI-LIBRARY asserts its own —
 * `intelligence-explanation-arms.test.tsx` mounts a hostile body and proves it
 * comes back as characters (the behavioural half, which can only speak about
 * the components it mounted), and `intelligence-untrusted-explanation.test.ts`
 * scans the whole feature directory for the escape hatch and for markdown/HTML
 * parsers (the structural half, which cannot be dodged by adding a file). A
 * hatch on a panel the render test does not mount would leave the render test
 * green, which is why one proof is not enough.
 *
 * THE ONE FIELD THAT IS NOT TEXT is `source.referenceUrl`, which becomes an
 * `href` — behaviour rather than characters, and the one place React's escaping
 * does not help. It goes through `safeHttpUrl`, an allow-list of two schemes.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE ONE PERMISSION CHECK, AND WHY IT IS NOT `if (!has(...))`.
 *
 * The reasoning itself needs no gate: it explains an answer this reader was
 * already authorized to receive, and the server withheld from it before it was
 * composed (`sourceWithheld`, `approvalWithheld`, `identityWithheld` are all
 * server decisions this file renders rather than makes).
 *
 * What DOES need one is the link out — "open this rule in the Library" lands on
 * `/library/rules/$ruleId`, a route gated on `knowledge:read`. That is an
 * EXISTING key on an EXISTING route; no key is invented here. And it is asked
 * through `authorityGate`, not `has()`, because `has()` returns `false` for
 * three unrelated reasons and only one of them is a refusal. Seven admin
 * screens already claim a refusal from a permission set that has not arrived
 * (`docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-FRONTEND.md`); this is not
 * the eighth. Unresolved and unknown both suppress the link — the safe
 * direction for a control — and only a RESOLVED set that lacks the key gets a
 * sentence, which is what `gateSentence` exists to word.
 */

import { Link } from '@tanstack/react-router';
import type { intelligence } from '@cwos/api-client';
import { usePermissions } from '../../permissions.js';
import { authorityGate, gateOpen, gateRefused, gateSentence } from '../../authorityGate.js';
import {
  approvalLine,
  causationNote,
  citationsFor,
  explanationState,
  factorView,
  ownerSentence,
  policyResolution,
  policySentence,
  policySourceLine,
  roleLabel,
  supportingEvidenceView,
  temporalView,
  uncertaintyLabel,
  type Citation,
  type PolicyResolution,
  type SupportingEvidenceView,
} from '../explanationView.js';
import { citationIndex } from '../evidenceView.js';

/** The key `/library/rules/$ruleId` and `/library/sources/$sourceId` charge. */
const LIBRARY_READ_KEY = 'knowledge:read';

type Explanation = intelligence.Explanation;
type PolicySourceElement = intelligence.ApplicablePolicy['elements'][number];

export function ExplanationPanel({
  verdict,
  evidence,
}: {
  verdict: intelligence.ExplanationVerdict | undefined;
  /**
   * THIS TURN'S SEALED MANIFEST — the thing every citation in the envelope
   * points AT, and the reason this component now takes a second prop.
   *
   * It is the same array `EvidencePanel` renders below, deliberately: the
   * citation numbers a claim carries and the numbered lines under "Based on
   * Phibian records" are one ordering of one object, so they cannot drift into
   * pointing at different rows.
   *
   * Optional and defaulted to empty, so a caller that has not been updated
   * renders exactly what it rendered before — markers suppressed, no claim
   * silently attributed to a manifest nobody passed.
   */
  evidence?: intelligence.TurnResponse['evidence'];
}) {
  const state = explanationState(verdict);

  // ABSENT. Nothing, deliberately and completely. See the docblock.
  if (state.kind === 'absent') return null;

  if (state.kind === 'rejected') {
    return (
      <div className="ap-explain ap-explain-rejected" data-explanation-arm="rejected">
        {/* ANNOUNCED. A guard fired on this turn's reasoning, which is an
            integrity event in exactly the sense `groundingCaution`'s 'alert'
            tone is — and unlike that one, it is the only signal in the whole
            response that says so, because the refused content is not on the
            wire. */}
        <p className="tc-warn ap-caution" role="alert">
          {state.text}
        </p>
      </div>
    );
  }

  return <ComposedExplanation explanation={state.explanation} evidence={evidence ?? []} />;
}

/**
 * ONE CITATION MARKER, or several, after the sentence they belong to.
 *
 * A NUMBER IN BRACKETS AND NEVER THE `ev_…` ID — see `citationsFor`. The
 * unresolved mark is styled apart because it is a different fact: a claim whose
 * retrieval is not in the record shown below is unverified, not uncited, and
 * the two must not look the same.
 *
 * The markers are `<sup>` inside the same paragraph as the claim, so a screen
 * reader reads "…fell by nine hours, based on record 2" rather than announcing
 * a stray number in its own region.
 */
function Cite({ citations }: { citations: readonly Citation[] }) {
  if (citations.length === 0) return null;
  return (
    <>
      {citations.map((citation) => (
        <sup
          key={citation.ref}
          className={
            citation.resolved
              ? 'ap-explain-cite'
              : 'ap-explain-cite ap-explain-cite-unresolved tc-warn'
          }
          data-citation={citation.label}
        >
          {/* The bracket characters are decorative; the sentence in the title
              and the visually-hidden span is what a non-sighted reader gets. */}
          <span aria-hidden="true" title={citation.description}>
            [{citation.label}]
          </span>
          <span className="ap-sr-only"> {citation.description}</span>
        </sup>
      ))}
    </>
  );
}

function ComposedExplanation({
  explanation,
  evidence,
}: {
  explanation: Explanation;
  evidence: intelligence.TurnResponse['evidence'];
}) {
  const temporal = temporalView(explanation.temporal);
  const owner = ownerSentence(explanation.owner);
  const policy = policyResolution(explanation.applicablePolicy);
  // ONE INDEX FOR THE WHOLE PANEL, built from the manifest the same response
  // carried. Every marker below resolves through it, so no two sections can
  // number the same retrieval differently.
  const index = citationIndex(evidence);
  /**
   * THE CITATIONS FOR ONE CLAIM — and the empty-manifest short circuit, which
   * is a four-state distinction and not a convenience.
   *
   * A ref that does not resolve against a manifest THAT EXISTS is a
   * disagreement between two halves of one response, and the reader is owed the
   * unresolved marker. A ref that does not resolve because there IS no manifest
   * is a different fact entirely — nothing was retrieved, which the grounding
   * caution above the panel already reports in the one place on the card meant
   * for how much to trust the answer. Drawing `[?]` on every sentence for that
   * would report one state twice, the second time as an integrity alarm.
   *
   * So: no manifest, no markers. A manifest that does not contain the ref, a
   * marker that says so.
   */
  const cite = (refs: readonly string[] | undefined): Citation[] =>
    evidence.length === 0 ? [] : citationsFor(refs, index);
  const factors = explanation.likelyFactors.map(factorView);
  const resting = supportingEvidenceView(explanation.supportingEvidence, index, evidence.length);

  return (
    <details className="ap-explain" data-explanation-arm="composed">
      <summary className="rn-link ap-explain-summary">Why Phibian says this</summary>
      <div className="ap-explain-body">
        <section className="ap-explain-section" aria-label="What happened">
          <p className="ap-explain-lede">
            {explanation.whatHappened.text}
            <Cite citations={cite(explanation.whatHappened.evidenceRefs)} />
          </p>
          <p className="rn-muted ap-explain-why">{explanation.whyItMatters}</p>
        </section>

        {explanation.whatChanged.length > 0 ? (
          <section className="ap-explain-section" aria-label="What changed">
            <h4 className="ap-explain-head">What changed</h4>
            <ul className="ap-explain-deltas">
              {explanation.whatChanged.map((delta, position) => (
                // `position` rather than `index`: the citation index is in scope
                // here and shadowing it with the array offset is how a marker
                // ends up pointing at the wrong retrieval.
                <li key={`${delta.metric}-${position}`}>
                  <span className="ap-explain-metric">{delta.metric}</span>{' '}
                  <span className="rn-muted">
                    {delta.from} → {delta.to}
                  </span>
                  <Cite citations={cite([delta.evidenceRef])} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="ap-explain-section" aria-label="Why">
          <h4 className="ap-explain-head">Why</h4>
          <p className="rn-muted ap-explain-causation">{causationNote(explanation)}</p>
          {factors.length === 0 ? (
            // AN EMPTY LIST IS AN ANSWER. The envelope was composed and named
            // no factor — different from the block being absent, which never
            // reaches this component, and different again from a refusal.
            <p className="rn-muted">Phibian didn’t identify anything in particular behind this.</p>
          ) : (
            <ul className="ap-explain-factors">
              {factors.map((factor, position) => (
                <li
                  key={`${factor.label}-${position}`}
                  className={
                    factor.unevidenced ? 'ap-explain-factor ap-explain-guess' : 'ap-explain-factor'
                  }
                >
                  <p className="ap-explain-factor-head">
                    <span className="ap-explain-basis">{factor.basisLabel}</span>
                    <span className="ap-explain-factor-label">{factor.label}</span>
                  </p>
                  <p className="ap-explain-factor-text">
                    {factor.statement}
                    {/* NO MARKER ON A GUESS, and the absence is the message.
                        `model_hypothesis` is the one basis the wire union gives
                        no `evidenceRef`, so there is nothing to point at — which
                        is what "nothing in your records stands behind this"
                        means, said again where a reader looks for a source. */}
                    <Cite
                      citations={factor.evidenceRef === undefined ? [] : cite([factor.evidenceRef])}
                    />
                  </p>
                  <p className="rn-muted ap-explain-basis-note">{factor.basisNote}</p>
                </li>
              ))}
            </ul>
          )}
          <p className="rn-muted ap-explain-uncertainty">
            <span className="ap-explain-basis">
              {uncertaintyLabel(explanation.uncertainty.level)}
            </span>{' '}
            {explanation.uncertainty.basis}
          </p>
        </section>

        <SupportingEvidence resting={resting} />

        <PolicySection resolution={policy} />

        {temporal !== null ? (
          <section className="ap-explain-section" aria-label="How current this is">
            <h4 className="ap-explain-head">How current this is</h4>
            <p
              className={temporal.tone === 'warn' ? 'tc-warn ap-caution' : 'rn-muted'}
              role={temporal.tone === 'warn' ? 'note' : undefined}
            >
              {temporal.text}
            </p>
            {/* COMPOSED IS NOT "AS OF". It is when a model ran, and labelling
                it "as of" would tell the reader the DATA was current then. */}
            <p className="rn-muted ap-explain-composed">
              This account was put together {new Date(temporal.composedAtMs).toLocaleString()}.
            </p>
          </section>
        ) : null}

        {explanation.recommendedAction !== undefined ? (
          <RecommendedAction action={explanation.recommendedAction} />
        ) : null}

        {owner !== null ? (
          <section className="ap-explain-section" aria-label="Who is accountable">
            <h4 className="ap-explain-head">Who is accountable</h4>
            <p className="rn-muted">{owner}</p>
          </section>
        ) : null}
      </div>
    </details>
  );
}

/**
 * §24 EVIDENCE — the retrievals the explanation as a whole rests on.
 *
 * THE ELEMENT THAT WAS ON THE WIRE AND ON NO SCREEN. `supportingEvidence` is
 * server-derived and hard-guarded: every ref in it resolved, at composition
 * time, to a retrieval this turn actually made and that actually accessed
 * something — a fabricated or out-of-turn ref rejects the whole envelope. None
 * of it was rendered anywhere in the admin bundle, so the guard protected a
 * claim no reader could follow.
 *
 * THREE STATES, KEPT APART, and the reason is the four-state rule this
 * repository keeps failing in one direction or another:
 *
 *   no_manifest — the turn made no retrievals at all. Renders NOTHING. The
 *     grounding caution twelve pixels above already tells this reader the
 *     answer was not checked against their records, and saying it again in the
 *     provenance slot is the sentence L182 deleted from the evidence panel for
 *     reading as an accusation.
 *   uncited     — retrievals happened and the explanation offered none of them
 *     as its shared basis. Renders a SENTENCE, because the reader cannot learn
 *     it from an empty list: an unbuilt list and an empty one look identical.
 *     The individual claims above may still carry their own markers, and this
 *     says only what it means.
 *   cited       — the numbers, pointing into the record below.
 */
function SupportingEvidence({ resting }: { resting: SupportingEvidenceView }) {
  if (resting.kind === 'no_manifest') return null;
  return (
    <section className="ap-explain-section" aria-label="What this rests on">
      <h4 className="ap-explain-head">What this rests on</h4>
      {resting.kind === 'uncited' ? (
        <p className="rn-muted">
          Phibian consulted your records for this answer and didn’t tie this account to any one of
          them. The numbered record is below.
        </p>
      ) : (
        <p className="rn-muted ap-explain-resting">
          Records
          <Cite citations={resting.citations} /> under “Based on Phibian records”, below.
        </p>
      )}
    </section>
  );
}

/**
 * §24 applicable policy — three states, and the date on two of them.
 *
 * `not_consulted` renders nothing: announcing "no policy was checked" on every
 * answer is the absent-as-rejected collapse again, one element down.
 * `none_applied` renders a SENTENCE and no list, because the fact is that the
 * corpus was asked and said nothing applies — which a reader can only learn
 * from words, since the list looks the same as the one that was never built.
 */
function PolicySection({ resolution }: { resolution: PolicyResolution }) {
  const permissions = usePermissions();
  const libraryGate = authorityGate(permissions, LIBRARY_READ_KEY);
  const mayOpen = gateOpen(libraryGate);
  // ONLY A RESOLVED SET GETS A SENTENCE. `unresolved` and `unknown` suppress
  // the link and say nothing, which is the safe direction and says nothing
  // false to anybody. See authorityGate.ts.
  const refusedNote = gateRefused(libraryGate)
    ? gateSentence(libraryGate, 'open these rules in the Library')
    : null;
  const sentence = policySentence(resolution);

  if (resolution.kind === 'not_consulted') return null;

  return (
    <section className="ap-explain-section" aria-label="Policy that applies">
      <h4 className="ap-explain-head">Policy that applies</h4>
      {sentence !== null ? <p className="rn-muted ap-explain-asof">{sentence}</p> : null}
      {resolution.kind === 'none_applied' ? null : (
        <>
          {resolution.constrainedByExternal ? (
            <p className="tc-warn ap-caution" role="note">
              Your organization’s own policy would have allowed more here, and it has been held to a
              requirement from outside that you cannot opt out of.
            </p>
          ) : null}
          {resolution.unresolvedContradictions.map((contradiction, index) => (
            <p className="tc-warn ap-caution" role="note" key={`contradiction-${index}`}>
              Two rules of equal standing on {contradiction.subject} pull in opposite directions and
              nothing here decided between them. Somebody has to.
            </p>
          ))}
          {resolution.reportableConflicts.map((conflict, index) => (
            <p className="tc-warn ap-caution" role="alert" key={`conflict-${index}`}>
              Your organization’s written policy on {conflict.subject} conflicts with a requirement
              you cannot opt out of. This is worth reporting.
            </p>
          ))}
          <ul className="ap-explain-rules">
            {resolution.elements.map((element) => (
              <RuleRow key={element.ruleId} element={element} mayOpen={mayOpen} />
            ))}
          </ul>
          {refusedNote !== null ? (
            <p className="rn-muted ap-explain-gate" role="note">
              {refusedNote}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function RuleRow({ element, mayOpen }: { element: PolicySourceElement; mayOpen: boolean }) {
  const source = policySourceLine(element);
  const approval = approvalLine(element);
  return (
    <li className="ap-explain-rule">
      <p className="ap-explain-rule-head">
        <span className="ap-explain-basis">{roleLabel(element.role)}</span>
        <span className="rn-muted">{element.subject}</span>
      </p>
      {/* THE RULE'S OWN WORDS, as a text node. This text came out of a tenant
          upload or an external ingestion and is quoted, never interpreted —
          `excerptUntrusted` masked, fenced and capped it server-side and React
          escapes it here.

          `element.rule` is an UntrustedExcerpt object, not a string. It was
          rendered as `{element.rule}` until 2026-09-07, which would have thrown
          "Objects are not valid as a React child" — except the api-client
          schema rejected the turn one layer earlier, so this line was never
          reached. Both halves are fixed together; fixing either alone moves the
          crash rather than removing it. */}
      <p className="ap-explain-rule-text">{element.rule.text}</p>
      {/* WHAT THE EXCERPT DECLARES ABOUT ITSELF. `excerpted` and `redacted` are
          facts a reader needs in order to judge what they are looking at: a
          capped rule is not the whole rule, and a masked one has had values
          removed. `instructionShaped` is a REVIEWER HINT and never a gate — the
          server delivers the excerpt identically either way, on the grounds
          that an operator governed by a rule is told the rule — so it is
          phrased as something to look at, not as a refusal. */}
      {element.rule.excerpted || element.rule.redacted || element.rule.instructionShaped ? (
        <p className="rn-muted ap-explain-rule-note" role="note">
          {element.rule.excerpted ? 'Shortened — this is not the whole rule. ' : ''}
          {element.rule.redacted ? 'Values that looked like personal data were masked out. ' : ''}
          {element.rule.instructionShaped
            ? 'This text contains instruction-shaped wording. Phibian quoted it as text and did not act on it; it is worth a look at the source document.'
            : ''}
        </p>
      ) : null}
      {source.kind === 'named' ? (
        <p className="rn-muted ap-explain-source">
          {mayOpen ? (
            <Link to="/library/sources/$sourceId" params={{ sourceId: source.sourceId }}>
              {source.title}
            </Link>
          ) : (
            <span>{source.title}</span>
          )}
          {source.detail !== null ? <span> · {source.detail}</span> : null}
          {source.referenceUrl !== null ? (
            <>
              {' · '}
              <a href={source.referenceUrl} rel="noreferrer noopener" target="_blank">
                Reference
              </a>
            </>
          ) : null}
        </p>
      ) : (
        <p className="rn-muted ap-explain-source">{source.text}</p>
      )}
      {approval !== null ? <p className="rn-muted ap-explain-approval">{approval}</p> : null}
      {mayOpen ? (
        <p className="ap-explain-rule-link">
          <Link to="/library/rules/$ruleId" params={{ ruleId: element.ruleId }}>
            Open this rule in the Library
          </Link>
        </p>
      ) : null}
    </li>
  );
}

/**
 * §24 recommended next action.
 *
 * READ OFF A DURABLE RECOMMENDATION RECORD, never written by the model — which
 * is why it is safe to show as a suggestion. `expectedEffect` is separately
 * optional because the record has no column for it yet, and its absence is
 * rendered as NOT STATED rather than inferred from the action text, which would
 * be this surface inventing the one element §24 says must be canonical.
 */
function RecommendedAction({ action }: { action: NonNullable<Explanation['recommendedAction']> }) {
  return (
    <section className="ap-explain-section" aria-label="What to do about it">
      <h4 className="ap-explain-head">What to do about it</h4>
      <p className="ap-explain-action">{action.action}</p>
      <p className="rn-muted ap-explain-action-note">
        {action.advisoryOnly
          ? 'This is advice. Nothing here does it for you.'
          : action.confirmationRequired
            ? 'If Phibian does this, it will ask you to confirm the exact change first.'
            : 'Phibian can do this through a named tool it is authorized for.'}{' '}
        Risk: {action.risk}.
      </p>
      <p className="rn-muted ap-explain-effect">
        {action.expectedEffect === undefined
          ? 'What this is expected to achieve isn’t recorded, so Phibian isn’t stating one.'
          : `Expected effect: ${action.expectedEffect}`}
      </p>
    </section>
  );
}
